<template>
  <div id="memo-table" v-if="asset">
    <div class="memo-container">
      <!-- Tab Navigation -->
      <nav class="tabs-nav">
        <button v-if="hasAbout" class="tab-btn" :class="{ active: activeTab === 'about' }" @click="activeTab = 'about'">
          <svg viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="7.5" stroke="currentColor" stroke-width="1.5"/><path d="M10 13V10M10 7H10.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
          About
        </button>
        <button v-if="hasMemo" class="tab-btn" :class="{ active: activeTab === 'memo' }" @click="activeTab = 'memo'">
          <svg viewBox="0 0 20 20" fill="none"><path d="M6.5 10L9 12.5L13.5 7.5M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
          Investment Memo
        </button>
        <button v-if="hasBusiness" class="tab-btn" :class="{ active: activeTab === 'business' }" @click="activeTab = 'business'">
          <svg viewBox="0 0 20 20" fill="none"><path d="M3 17V6C3 4.89543 3.89543 4 5 4H15C16.1046 4 17 4.89543 17 6V17M3 17H17M3 17H1M17 17H19M7 8H9M11 8H13M7 12H9M11 12H13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
          Business Info
        </button>
        <button v-if="hasDocs" class="tab-btn" :class="{ active: activeTab === 'docs' }" @click="activeTab = 'docs'">
          <svg viewBox="0 0 20 20" fill="none"><path d="M6 2H11L16 7V16C16 17.1046 15.1046 18 14 18H6C4.89543 18 4 17.1046 4 16V4C4 2.89543 4.89543 2 6 2Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M11 2V7H16" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
          Docs
        </button>
        <button v-if="hasOrderBook" class="tab-btn" :class="{ active: activeTab === 'orderbook' }" @click="activeTab = 'orderbook'">
          <svg viewBox="0 0 20 20" fill="none"><path d="M3 5H17M3 10H17M3 15H12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
          Order Book
        </button>
      </nav>

      <!-- Tab Content -->
      <div class="tab-content">
        <!-- About Tab -->
        <div v-if="activeTab === 'about'" class="tab-panel">
          <div v-if="asset.about" class="html-text" v-html="asset.about"></div>
          <div v-else class="empty-state">
            <div class="empty-icon">
              <svg viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.5"/><path d="M12 16V12M12 8H12.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
            </div>
            <p class="empty-text">No description available</p>
          </div>
        </div>

        <!-- Investment Memo Tab -->
        <div v-if="activeTab === 'memo'" class="tab-panel">
          <!-- Simple Mode: Only memo exists -->
          <div v-if="onlyMemo" class="html-text" v-html="asset.memo?.memo"></div>
          
          <!-- Accordion Mode: Multiple sections -->
          <div v-else class="memo-sections">
            <div v-if="asset.memo?.memo" class="memo-section">
              <button class="section-header" @click="toggleSection('memo')">
                <span class="section-title">Investment Memo</span>
                <svg class="chevron" :class="{ open: openSections.memo }" viewBox="0 0 16 16" fill="none"><path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </button>
              <div v-if="openSections.memo" class="section-body">
                <div class="html-text" v-html="asset.memo.memo"></div>
              </div>
            </div>
            <div v-if="asset.memo?.overview" class="memo-section">
              <button class="section-header" @click="toggleSection('overview')">
                <span class="section-title">Overview</span>
                <svg class="chevron" :class="{ open: openSections.overview }" viewBox="0 0 16 16" fill="none"><path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </button>
              <div v-if="openSections.overview" class="section-body">
                <div class="html-text" v-html="asset.memo.overview"></div>
              </div>
            </div>
            <div v-if="asset.memo?.problem" class="memo-section">
              <button class="section-header" @click="toggleSection('problem')">
                <span class="section-title">Problem</span>
                <svg class="chevron" :class="{ open: openSections.problem }" viewBox="0 0 16 16" fill="none"><path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </button>
              <div v-if="openSections.problem" class="section-body">
                <div class="html-text" v-html="asset.memo.problem"></div>
              </div>
            </div>
            <div v-if="asset.memo?.solution" class="memo-section">
              <button class="section-header" @click="toggleSection('solution')">
                <span class="section-title">Solution</span>
                <svg class="chevron" :class="{ open: openSections.solution }" viewBox="0 0 16 16" fill="none"><path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </button>
              <div v-if="openSections.solution" class="section-body">
                <div class="html-text" v-html="asset.memo.solution"></div>
              </div>
            </div>
            <div v-if="asset.memo?.opportunity" class="memo-section">
              <button class="section-header" @click="toggleSection('opportunity')">
                <span class="section-title">Market Opportunity</span>
                <svg class="chevron" :class="{ open: openSections.opportunity }" viewBox="0 0 16 16" fill="none"><path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </button>
              <div v-if="openSections.opportunity" class="section-body">
                <div class="html-text" v-html="asset.memo.opportunity"></div>
              </div>
            </div>
            <div v-if="asset.memo?.traction" class="memo-section">
              <button class="section-header" @click="toggleSection('traction')">
                <span class="section-title">Traction</span>
                <svg class="chevron" :class="{ open: openSections.traction }" viewBox="0 0 16 16" fill="none"><path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </button>
              <div v-if="openSections.traction" class="section-body">
                <div class="html-text" v-html="asset.memo.traction"></div>
              </div>
            </div>
            <div v-if="asset.memo?.team" class="memo-section">
              <button class="section-header" @click="toggleSection('team')">
                <span class="section-title">Team</span>
                <svg class="chevron" :class="{ open: openSections.team }" viewBox="0 0 16 16" fill="none"><path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </button>
              <div v-if="openSections.team" class="section-body">
                <div class="html-text" v-html="asset.memo.team"></div>
              </div>
            </div>
          </div>
        </div>

        <!-- Business Information Tab -->
        <div v-if="activeTab === 'business'" class="tab-panel">
          <!-- Funding Section -->
          <div v-if="asset.memo?.previously_raised" class="info-section">
            <h3 class="info-title">Funding</h3>
            <div class="info-grid">
              <div class="info-item">
                <span class="info-label">Previously Raised?</span>
                <span class="info-value">{{ asset.memo?.previously_raised ? 'Yes' : 'No' }}</span>
              </div>
              <div v-if="asset.memo?.previously_raised && asset.memo?.previously_raised_amount" class="info-item">
                <span class="info-label">Amount Previously Raised</span>
                <span class="info-value">{{ asset.memo?.previously_raised_amount }}</span>
              </div>
              <div v-if="asset?.stage" class="info-item">
                <span class="info-label">Stage of Funding</span>
                <span class="info-value">{{ asset?.stage }}</span>
              </div>
            </div>
          </div>

          <!-- Competitors Section -->
          <div v-if="asset.memo?.competitors?.length" class="info-section">
            <h3 class="info-title">Competitors</h3>
            <div class="competitors-grid">
              <div v-for="(competitor, index) in asset.memo.competitors" :key="index" class="competitor-item">
                <span class="competitor-label">Competitor {{ index + 1 }}</span>
                <span class="competitor-name">{{ competitor }}</span>
              </div>
            </div>
          </div>

          <!-- Pitch Video Section -->
          <div v-if="pitchVideo" class="info-section">
            <h3 class="info-title">Pitch Video</h3>
            <div class="video-container">
              <iframe class="video-frame" :src="pitchVideo" title="Pitch Video" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>
            </div>
            <div class="video-link-box">
              <input type="text" readonly :value="pitchVideo" class="video-link-input" ref="pitchDeckVideo" @focus="$event.target.select()" />
              <button class="copy-btn" @click="copyText">
                <svg viewBox="0 0 16 16" fill="none"><rect x="5" y="5" width="9" height="9" rx="1" stroke="currentColor" stroke-width="1.5"/><path d="M11 5V3C11 2.44772 10.5523 2 10 2H3C2.44772 2 2 2.44772 2 3V10C2 10.5523 2.44772 11 3 11H5" stroke="currentColor" stroke-width="1.5"/></svg>
                Copy Link
              </button>
            </div>
          </div>

          <!-- Empty State -->
          <div v-if="!asset.memo?.previously_raised && !asset.memo?.competitors?.length && !pitchVideo" class="empty-state">
            <div class="empty-icon">
              <svg viewBox="0 0 24 24" fill="none"><path d="M3 21V8C3 6.89543 3.89543 6 5 6H19C20.1046 6 21 6.89543 21 8V21M3 21H21M3 21H1M21 21H23M8 10H10M14 10H16M8 14H10M14 14H16" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
            </div>
            <p class="empty-text">No business information available</p>
          </div>
        </div>

        <!-- Supporting Documents Tab -->
        <div v-if="activeTab === 'docs'" class="tab-panel">
          <div v-if="asset.documents?.length" class="docs-grid">
            <a v-for="(doc, index) in asset.documents" :key="index" :href="doc.url" target="_blank" rel="noopener" class="doc-card">
              <div class="doc-icon">
                <svg viewBox="0 0 24 24" fill="none"><path d="M7 2H14L20 8V20C20 21.1046 19.1046 22 18 22H7C5.89543 22 5 21.1046 5 20V4C5 2.89543 5.89543 2 7 2Z" stroke="currentColor" stroke-width="1.5"/><path d="M14 2V8H20" stroke="currentColor" stroke-width="1.5"/></svg>
              </div>
              <span class="doc-name">{{ doc.name }}</span>
              <svg class="doc-arrow" viewBox="0 0 16 16" fill="none"><path d="M4 12L12 4M12 4H6M12 4V10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </a>
          </div>
          <div v-else class="empty-state">
            <div class="empty-icon">
              <svg viewBox="0 0 24 24" fill="none"><path d="M7 2H14L20 8V20C20 21.1046 19.1046 22 18 22H7C5.89543 22 5 21.1046 5 20V4C5 2.89543 5.89543 2 7 2Z" stroke="currentColor" stroke-width="1.5"/><path d="M14 2V8H20" stroke="currentColor" stroke-width="1.5"/></svg>
            </div>
            <p class="empty-text">No documents available</p>
          </div>
        </div>

        <!-- Order Book Tab -->
        <div v-if="activeTab === 'orderbook'" class="tab-panel">
          <div v-if="sellOrderBook.length > 0 || buyOrderBook.length > 0">
            <!-- Order Type Toggle -->
            <div class="order-tabs">
              <button class="order-tab" :class="{ active: orderType === 'sell' }" @click="orderType = 'sell'">
                <span class="tab-dot sell"></span>
                Sell Orders
                <span v-if="sellOrderBook.length" class="tab-count">{{ sellOrderBook.length }}</span>
              </button>
              <button class="order-tab" :class="{ active: orderType === 'buy' }" @click="orderType = 'buy'">
                <span class="tab-dot buy"></span>
                Buy Orders
                <span v-if="buyOrderBook.length" class="tab-count">{{ buyOrderBook.length }}</span>
              </button>
            </div>

            <!-- Sell Orders Table -->
            <div v-if="orderType === 'sell'" class="orders-table">
              <div v-if="sellOrderBook.length > 0">
                <div class="table-header">
                  <span>Price/Share</span>
                  <span>Shares</span>
                  <span>Total Value</span>
                </div>
                <div class="table-body">
                  <div v-for="(order, index) in sellOrderBook" :key="'sell-' + index" class="table-row">
                    <span class="price sell">{{ formatAmountToDollar(Number(order.price), 2, order.token?.currency || 'USD') }}</span>
                    <span class="shares">
                      <svg viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6" stroke="currentColor" stroke-width="1.5"/><path d="M8 5V11M5 8H11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
                      {{ convertToLocale(Number(order.amount)) }}
                    </span>
                    <span class="total">{{ formatAmountToDollar(Number(order.amount * order.price), 2, order.token?.currency || 'USD') }}</span>
                  </div>
                </div>
              </div>
              <div v-else class="empty-state">
                <div class="empty-icon">
                  <svg viewBox="0 0 24 24" fill="none"><path d="M3 5H21M3 12H21M3 19H15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
                </div>
                <p class="empty-text">No sell orders available</p>
              </div>
            </div>

            <!-- Buy Orders Table -->
            <div v-if="orderType === 'buy'" class="orders-table">
              <div v-if="buyOrderBook.length > 0">
                <div class="table-header">
                  <span>Price/Share</span>
                  <span>Shares</span>
                  <span>Total Value</span>
                </div>
                <div class="table-body">
                  <div v-for="(order, index) in buyOrderBook" :key="'buy-' + index" class="table-row">
                    <span class="price buy">{{ formatAmountToDollar(Number(order.price), 2, order.token?.currency || 'USD') }}</span>
                    <span class="shares">
                      <svg viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6" stroke="currentColor" stroke-width="1.5"/><path d="M8 5V11M5 8H11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
                      {{ convertToLocale(Number(order.amount)) }}
                    </span>
                    <span class="total">{{ formatAmountToDollar(Number(order.amount * order.price), 2, order.token?.currency || 'USD') }}</span>
                  </div>
                </div>
              </div>
              <div v-else class="empty-state">
                <div class="empty-icon">
                  <svg viewBox="0 0 24 24" fill="none"><path d="M3 5H21M3 12H21M3 19H15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
                </div>
                <p class="empty-text">No buy orders available</p>
              </div>
            </div>
          </div>
          <div v-else class="empty-state">
            <div class="empty-icon">
              <svg viewBox="0 0 24 24" fill="none"><path d="M3 5H21M3 12H21M3 19H15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
            </div>
            <p class="empty-text">No orders available</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { convertToLocale, formatAmountToDollar } from '@/utils/helpers';
import { mapActions, mapState } from 'vuex';

export default {
  name: 'MemoTable',
  data() {
    return {
      activeTab: '',
      orderType: 'sell',
      openSections: {
        memo: true,
        overview: false,
        problem: false,
        solution: false,
        opportunity: false,
        traction: false,
        team: false,
      },
    };
  },
  computed: {
    ...mapState({
      asset: (state) => state.TokenModule.assetInfo,
      token: (state) => state.TokenModule.tokenInfo,
      buyOrderBook: (state) => state.TokenModule.buyOrderBook || [],
      sellOrderBook: (state) => state.TokenModule.sellOrderBook || [],
    }),
    pitchVideo() {
      return this.asset?.memo?.pitch_url;
    },
    hasAbout() {
      return !!this.asset?.about;
    },
    hasMemo() {
      const memo = this.asset?.memo;
      return !!memo?.overview || !!memo?.problem || !!memo?.solution || !!memo?.traction || !!memo?.team || !!memo?.memo;
    },
    hasBusiness() {
      return !!this.asset?.memo?.previously_raised || !!this.pitchVideo || (this.asset?.memo?.competitors?.length > 0);
    },
    hasDocs() {
      return this.asset?.documents?.length > 0;
    },
    hasOrderBook() {
      return this.sellOrderBook.length > 0 || this.buyOrderBook.length > 0;
    },
    onlyMemo() {
      const memo = this.asset?.memo;
      return !memo?.overview && !memo?.problem && !memo?.solution && !memo?.traction && !memo?.team && !!memo?.memo;
    },
  },
  watch: {
    asset: {
      immediate: true,
      handler() {
        this.setDefaultTab();
      },
    },
    sellOrderBook() {
      if (this.sellOrderBook.length === 0 && this.buyOrderBook.length > 0) {
        this.orderType = 'buy';
      }
    },
  },
  mounted() {
    this.fetchOrderBook();
  },
  methods: {
    ...mapActions('TokenModule', ['getTokenOrders']),
    formatAmountToDollar,
    convertToLocale,
    setDefaultTab() {
      if (this.hasAbout) this.activeTab = 'about';
      else if (this.hasMemo) this.activeTab = 'memo';
      else if (this.hasBusiness) this.activeTab = 'business';
      else if (this.hasDocs) this.activeTab = 'docs';
      else if (this.hasOrderBook) this.activeTab = 'orderbook';
    },
    toggleSection(section) {
      this.openSections[section] = !this.openSections[section];
    },
    copyText() {
      this.$refs.pitchDeckVideo?.select();
      navigator.clipboard?.writeText(this.pitchVideo);
    },
    fetchOrderBook() {
      const id = this.token?._id || this.$route.params.id;
      if (!id) return;
      this.getTokenOrders({ id, data: { page: 1, type: 'Buy', limit: 10, status: 'Active' } });
      this.getTokenOrders({ id, data: { page: 1, type: 'Sell', limit: 10, status: 'Active' } });
    },
  },
};
</script>

<style lang="scss" scoped>
$bg-primary: #F8FAFC;
$bg-card: #FFFFFF;
$bg-card-hover: #F1F5F9;
$border-color: #E2E8F0;
$text-primary: #0F172A;
$text-secondary: #475569;
$text-muted: #94A3B8;
$accent: #4D84FF;
$btn-primary: #001240;
$success: #10B981;
$error: #EF4444;

#memo-table { width: 100%; }

.memo-container { background: $bg-card; border-radius: 10px; overflow: hidden; }

// Tab Navigation
.tabs-nav {
  display: flex;
  gap: 4px;
  padding: 12px 16px;
  background: $bg-primary;
  border-bottom: 1px solid $border-color;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }

  @media (min-width: 768px) { padding: 16px 24px; }
}

.tab-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 400;
  color: $text-secondary;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.2s ease;

  svg { width: 18px; height: 18px; flex-shrink: 0; }

  &:hover { background: $bg-card; color: $text-primary; }

  &.active {
    background: $bg-card;
    border-color: $border-color;
    color: $btn-primary;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  }
}

// Tab Content
.tab-content { padding: 24px 20px; @media (min-width: 768px) { padding: 32px; } }
.tab-panel { animation: fadeIn 0.2s ease; }
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

// HTML Text Styling
.html-text {
  line-height: 1.8;
  font-size: 0.9375rem;
  color: $text-secondary;

  :deep(p) { margin: 1em 0; line-height: 1.8; }
  :deep(h1), :deep(h2), :deep(h3), :deep(h4), :deep(h5), :deep(h6) {
    margin-top: 1.5em;
    margin-bottom: 0.75em;
    font-weight: 400;
    color: $text-primary;
  }
  :deep(a) { color: $accent; text-decoration: underline; }
  :deep(ul), :deep(ol) { margin: 1em 0; padding-left: 1.75em; }
  :deep(li) { margin: 0.5em 0; }
  :deep(blockquote) {
    padding: 1em 1.25em;
    margin: 1em 0;
    border-left: 4px solid $border-color;
    background: $bg-primary;
    border-radius: 0 8px 8px 0;
  }
  :deep(img) { max-width: 100%; border-radius: 10px; margin: 1em 0; }
  :deep(pre), :deep(code) {
    background: $bg-primary;
    padding: 0.25em 0.5em;
    border-radius: 4px;
    font-size: 0.875em;
  }
  :deep(pre) { padding: 1em; overflow-x: auto; }
  :deep(table) { width: 100%; border-collapse: collapse; margin: 1em 0; }
  :deep(th), :deep(td) { padding: 0.75em; border: 1px solid $border-color; text-align: left; }
  :deep(th) { background: $bg-primary; font-weight: 500; }
}

// Empty State
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
}
.empty-icon {
  width: 48px;
  height: 48px;
  margin-bottom: 16px;
  color: $text-muted;
  svg { width: 100%; height: 100%; }
}
.empty-text { font-size: 14px; color: $text-muted; margin: 0; }

// Memo Accordion Sections
.memo-sections { display: flex; flex-direction: column; gap: 12px; }
.memo-section { border: 1px solid $border-color; border-radius: 10px; overflow: hidden; }
.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 16px 20px;
  background: $bg-primary;
  border: none;
  cursor: pointer;
  transition: background 0.2s ease;
  &:hover { background: $bg-card-hover; }
}
.section-title { font-size: 15px; font-weight: 400; color: $text-primary; }
.chevron {
  width: 16px;
  height: 16px;
  color: $text-muted;
  transition: transform 0.2s ease;
  &.open { transform: rotate(180deg); }
}
.section-body { padding: 20px; border-top: 1px solid $border-color; background: $bg-card; }

// Business Info Section
.info-section {
  padding-bottom: 24px;
  margin-bottom: 24px;
  border-bottom: 1px solid $border-color;
  &:last-child { padding-bottom: 0; margin-bottom: 0; border-bottom: none; }
}
.info-title { font-size: 16px; font-weight: 500; color: $text-primary; margin: 0 0 16px 0; }
.info-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 16px; }
.info-item { display: flex; flex-direction: column; gap: 4px; }
.info-label { font-size: 13px; color: $text-muted; }
.info-value { font-size: 15px; color: $text-primary; }

.competitors-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 12px; }
.competitor-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px;
  background: $bg-primary;
  border-radius: 8px;
}
.competitor-label { font-size: 12px; color: $text-muted; }
.competitor-name { font-size: 14px; color: $text-secondary; }

.video-container { margin-bottom: 16px; }
.video-frame { width: 100%; height: 320px; border-radius: 10px; @media (min-width: 768px) { height: 400px; } }
.video-link-box {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: $bg-primary;
  border-radius: 8px;
}
.video-link-input {
  flex: 1;
  background: transparent;
  border: none;
  font-size: 14px;
  color: $text-secondary;
  outline: none;
  overflow: hidden;
  text-overflow: ellipsis;
}
.copy-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: $btn-primary;
  border: none;
  border-radius: 4px;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.2s ease;
  svg { width: 14px; height: 14px; }
  &:hover { background: darken($btn-primary, 10%); }
}

// Documents Grid
.docs-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 16px; }
.doc-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 24px 16px;
  background: $bg-primary;
  border: 1px solid $border-color;
  border-radius: 10px;
  text-decoration: none;
  transition: all 0.2s ease;
  position: relative;
  &:hover { border-color: $accent; background: $bg-card; }
}
.doc-icon { width: 40px; height: 40px; color: $accent; svg { width: 100%; height: 100%; } }
.doc-name { font-size: 14px; color: $text-primary; text-align: center; word-break: break-word; }
.doc-arrow {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 16px;
  height: 16px;
  color: $text-muted;
  opacity: 0;
  transition: opacity 0.2s ease;
}
.doc-card:hover .doc-arrow { opacity: 1; color: $accent; }

// Order Book
.order-tabs {
  display: flex;
  gap: 4px;
  padding: 4px;
  background: $bg-primary;
  border-radius: 8px;
  margin-bottom: 16px;
}
.order-tab {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  background: transparent;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  color: $text-secondary;
  cursor: pointer;
  transition: all 0.2s ease;

  &.active { background: $bg-card; color: $text-primary; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08); }
}
.tab-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  &.sell { background: $error; }
  &.buy { background: $success; }
}
.tab-count {
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  background: rgba($accent, 0.1);
  color: $accent;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
}

.orders-table { border: 1px solid $border-color; border-radius: 8px; overflow: hidden; }
.table-header {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
  padding: 12px 16px;
  background: $bg-primary;
  border-bottom: 1px solid $border-color;

  span {
    font-size: 12px;
    font-weight: 500;
    color: $text-muted;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
}
.table-body { max-height: 320px; overflow-y: auto; }
.table-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid $border-color;
  &:last-child { border-bottom: none; }
  &:hover { background: rgba($bg-primary, 0.5); }
}
.price {
  font-size: 14px;
  font-weight: 500;
  &.sell { color: $error; }
  &.buy { color: $success; }
}
.shares {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: $text-primary;
  svg { width: 16px; height: 16px; color: $text-muted; }
}
.total { font-size: 14px; font-weight: 500; color: $text-primary; }

// Mobile Responsive
@media (max-width: 640px) {
  .order-tab { padding: 8px 12px; font-size: 13px; }
  .table-header span { font-size: 11px; }
  .price, .shares, .total { font-size: 13px; }
}
</style>
